import { Component, ComponentInterface, Event, EventEmitter, h, Prop } from '@stencil/core';
import { JuiControlSize } from '../../../shared/form-controls/jui-form-control.model';

@Component({
  tag: 'jui-checkbox',
  styleUrl: 'jui-checkbox.scss',
  shadow: true,
  formAssociated: true
})
export class JuiCheckbox implements ComponentInterface {
  /**
   * Label to display for the radio input.
   */
  @Prop() label = '';

  /**
   * Value to hold the current value for the checkbox.
   */
  @Prop({ attribute: 'value', reflect: true, mutable: true }) value = '';

  /**
   * The form name for the checkbox
   */
  @Prop() name = '';

  /**
   * If this checkbox is not part of a group
   */
  @Prop() single = true;

  /**
   * Marking the checkbox as disabled. Defaults to false
   */
  @Prop() disable: boolean;

  /**
   * Set the radio input as checked. Defaults to false.
   */
  @Prop({ attribute: 'checked', reflect: true })
  checked = false;

  /**
   * Emits the juiChecked or juiChipChecked event on first load. Defaults to true
   */
  @Prop()
  emiteventonload = true;

  /**
   * Display the checkbox as a toggle
   */
  @Prop()
  toggle = false;

  /**
   * Optional text to display in the toggle state when the toggle is off.
   * Only displayed if 'toggle' is set to true.
   */
  @Prop()
  toggleofftext = '';

  /**
   * Optional text to display in the toggle state when the toggle is on.
   * Only displayed if 'toggle' is set to true.
   */
  @Prop()
  toggleontext = '';

  /**
   * Set the size of the checkbox. Defaults to md.
   */
  @Prop() size: JuiControlSize = JuiControlSize.md;

  @Event() public juiChecked: EventEmitter<boolean>;
  @Event() public juiCheckboxChecked: EventEmitter<{ value: string; checked: boolean }>;

  render() {
    const inputFragment = (
      <input
        role="checkbox"
        type="checkbox"
        class={{ 'jui-checkbox__input': true, [`jui-checkbox__input--${this.size}`]: true }}
        checked={this.checked}
        value={this.value}
        disabled={this.disable}
        name={this.name}
        id={this.label}
        onClick={this.onClick.bind(this)}
      ></input>
    );
    const toggleSpanFragment = (
      <span
        class={{
          'jui-checkbox__toggle': true,
          'jui-checkbox__toggle--checked': this.checked,
          [`jui-checkbox__toggle--${this.size}`]: true
        }}
      ></span>
    );
    const toggleTextFragment =
      this.toggleontext && this.toggleofftext ? (
        <div
          class={{
            'jui-checkbox__toggle-text': true,
            [`jui-checkbox__toggle-text--${this.size}`]: true
          }}
        >
          {this.checked ? this.toggleontext : this.toggleofftext}
        </div>
      ) : null;
    const labelFragment = this.label ? (
      <label
        htmlFor={this.label}
        class={{
          'jui-checkbox__label': true,
          'jui-checkbox__label--toggle': this.toggle,
          [`jui-checkbox__label--${this.size}`]: true,
          [`jui-checkbox__label--disabled`]: !!this.disable
        }}
      >
        {this.label}
      </label>
    ) : null;
    const labelToggleFragment = (
      <label
        htmlFor={this.label}
        class={{
          'jui-checkbox__label': true,
          'jui-checkbox__label--toggle': this.toggle,
          [`jui-checkbox__label--${this.size}`]: true,
          [`jui-checkbox__label--disabled`]: !!this.disable
        }}
      >
        <div class="jui-checkbox__toggle-container">
          <span>{this.label}</span>
          <div class="jui-checkbox__toggle-wrapper">
            {inputFragment}
            <div class={{
              'jui-checkbox__toggle-slider': true,
              [`jui-checkbox__toggle-slider--${this.size}`]: true
            }}>
            </div>
            {toggleSpanFragment}
            {toggleTextFragment}
          </div>
        </div>
      </label>
    );
    return this.toggle ? (
      <div class={{ 'jui-checkbox': true, [`jui-checkbox--flex`]: !!this.label }}>{labelToggleFragment}</div>
    ) : (
      <div class={{ 'jui-checkbox': true, [`jui-checkbox--flex`]: !!this.label }}>
        {inputFragment}
        {labelFragment}
      </div>
    );
  }

  componentWillLoad(): void {
    if (!this.value) {
      this.value = this.label;
    }
  }

  componentDidLoad(): void {
    if (!this.emiteventonload) {
      return;
    }
    if (this.single) {
      this.juiChecked.emit(this.checked);
    } else {
      this.juiCheckboxChecked.emit({ value: this.value, checked: this.checked });
    }
  }

  private onClick(): void {
    this.checked = !this.checked;
    if (this.single) {
      this.juiChecked.emit(this.checked);
    } else {
      this.juiCheckboxChecked.emit({ value: this.value, checked: this.checked });
    }
  }
}
